import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const trayBakeRecipes: RecipeFamily[] = [
  {
    id: "honey-mustard-chickpea-tray-bake",
    nameTemplate: "Sweet & tangy {protein.name} & {vegetable.name} tray bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 40,
    slots: [
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 480, "g", "drained and dried", 3, undefined, {
          tr: { preparation: "süzülmüş ve kurulanmış" },
        }),
        option("tofu", 300, "g", "pressed and cubed", 2, undefined, {
          tr: { preparation: "sıkılmış ve küp doğranmış" },
        }),
      ]),
      slot("vegetable", "Roasting vegetable", true, "sweet-potato", [
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 3, undefined, {
          tr: { preparation: "2 cm'lik küpler halinde doğranmış" },
        }),
        option("carrot", 400, "g", "cut into batons", 2, undefined, {
          tr: { preparation: "çubuk şeklinde kesilmiş" },
        }),
      ]),
      slot("sauce", "Sweet-tangy glaze", true, "maple-syrup", [
        option("maple-syrup", 2, "tbsp", undefined, 3),
        option("balsamic-vinegar", 2, "tbsp", undefined, 2),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 2, "tbsp")]),
      slot("spice", "Spice", true, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("finish", "Fresh finish", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3, undefined, {
          tr: { preparation: "doğranmış" },
        }),
        option("lemon", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 210°C.",
        translations: { tr: { template: "Fırını 210°C'ye ısıtın." } },
      },
      {
        id: "toss-veg",
        template:
          "Toss {vegetable.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name} on a large baking sheet. Roast for 15 minutes.",
        translations: {
          tr: {
            template:
              "{vegetable.name} üzerine {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} gezdirin. Büyük bir tepsiye alın. 15 dakika kızartın.",
          },
        },
      },
      {
        id: "add-protein",
        template:
          "Add {protein.name} to the sheet. Drizzle {sauce.name} over everything and toss to coat. Roast for a further 15 to 20 minutes until golden and sticky.",
        translations: {
          tr: {
            template:
              "Tepsiye {protein.name} ekleyin. Her şeyin üzerine {sauce.name} gezdirin ve karıştırın. 15-20 dakika daha altın rengi ve yapışkanlı olana kadar kızartın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {finish.name} over the top if using and serve directly from the tray.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} üzerine serpin ve doğrudan tepsiden servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "sweet", "acid", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Tatlı & ekşi {protein.name} & {vegetable.name} fırın yemeği",
      },
    },
  },
  {
    id: "miso-glazed-eggplant-tray-bake",
    nameTemplate: "Glazed {vegetable.name} & {protein.name} tray bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 35,
    slots: [
      slot("vegetable", "Main vegetable", true, "eggplant", [
        option("eggplant", 450, "g", "cut into thick wedges", 3, undefined, {
          tr: { preparation: "kalın dilimler halinde kesilmiş" },
        }),
        option("zucchini", 450, "g", "halved lengthways", 2, undefined, {
          tr: { preparation: "boyuna ikiye kesilmiş" },
        }),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cubed", 3, undefined, {
          tr: { preparation: "sıkılmış ve küp doğranmış" },
        }),
        option("edamame", 150, "g", "shelled", 2, undefined, {
          tr: { preparation: "kabuğu çıkarılmış" },
        }),
      ]),
      slot("glaze", "Glaze", true, "miso", [
        option("miso", 2, "tbsp"),
        option("soy-sauce", 3, "tbsp"),
      ]),
      slot("sweetener", "Sweetener", true, "maple-syrup", [
        option("maple-syrup", 1, "tbsp"),
        option("balsamic-vinegar", 1, "tbsp"),
      ]),
      slot("oil", "Oil", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
        option("noodles", 160, "g", "cooked", 2, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("finish", "Garnish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", undefined, 3),
        option("scallion", 2, "piece", "thinly sliced", 2, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      pantry.salt,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye ısıtın." } },
      },
      {
        id: "make-glaze",
        template:
          "Whisk together {glaze.name}, {sweetener.name}, and {oil.name} in a small bowl.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {glaze.name}, {sweetener.name} ve {oil.name} çırpın.",
          },
        },
      },
      {
        id: "roast",
        template:
          "Place {vegetable.name} and {protein.name} on a lined baking sheet. Brush or spoon the glaze generously over everything. Roast for 20 to 25 minutes, brushing with remaining glaze halfway through.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve {protein.name} yağlı kağıt serili tepsiye koyun. Sosu her şeyin üzerine cömertçe sürün. Yarısında kalan sosu sürerek 20-25 dakika kızartın.",
          },
        },
      },
      {
        id: "serve",
        template: "Serve over {base.name} and scatter {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin ve kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "sweet", "fat", "protein", "starch"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Sırlı {vegetable.name} & {protein.name} fırın yemeği",
      },
    },
  },
  {
    id: "roasted-cauliflower-chickpea-tray-bake",
    nameTemplate: "{vegetable.name} & {protein.name} spiced tray bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 40,
    slots: [
      slot("vegetable", "Main vegetable", true, "cauliflower", [
        option(
          "cauliflower",
          450,
          "g",
          "cut into small florets",
          3,
          undefined,
          {
            tr: { preparation: "küçük demetlere ayrılmış" },
          },
        ),
        option("broccoli", 450, "g", "cut into small florets", 2, undefined, {
          tr: { preparation: "küçük demetlere ayrılmış" },
        }),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 480, "g", "drained and dried", 3, undefined, {
          tr: { preparation: "süzülmüş ve kurulanmış" },
        }),
        option("lentils", 200, "g", "cooked", 2, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      slot("spice", "Spice blend", true, "cumin", [
        option("cumin", 2, "tsp", undefined, 3),
        option("smoked-paprika", 2, "tsp", undefined, 2),
      ]),
      slot("sauce", "Sauce", true, "tahini", [
        option("tahini", 3, "tbsp"),
        option("yogurt", 3, "tbsp"),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("balsamic-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3, undefined, {
          tr: { preparation: "doğranmış" },
        }),
        option("pomegranate", 50, "g", "seeds", 2, undefined, {
          tr: { preparation: "taneleri" },
        }),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 210°C.",
        translations: { tr: { template: "Fırını 210°C'ye ısıtın." } },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name} and {protein.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name}. Spread on a large baking sheet and roast for 25 to 30 minutes, tossing halfway through, until golden and crispy.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve {protein.name} üzerine {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} gezdirin. Büyük bir tepsiye yayın ve yarısında karıştırarak 25-30 dakika altın rengi ve çıtır olana kadar kızartın.",
          },
        },
      },
      {
        id: "make-sauce",
        template:
          "Whisk together {sauce.name}, {acid.name}, and a splash of water until pourable. Season with {salt.name}.",
        translations: {
          tr: {
            template:
              "{sauce.name} ve {acid.name} az miktarda su ile akışkan olana kadar çırpın. {salt.name} ekleyin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Drizzle the sauce over the roasted vegetables and {finish.name} if using. Serve directly from the tray.",
        translations: {
          tr: {
            template:
              "Sosu kızartılmış sebzeler üzerine gezdirin ve kullanıyorsanız {finish.name} ekleyin. Doğrudan tepsiden servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "protein", "fat", "aroma", "acid"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Baharatlı {vegetable.name} & {protein.name} fırın yemeği",
      },
    },
  },
  {
    id: "mediterranean-tray-bake",
    nameTemplate: "Mediterranean {vegetable.name} & {protein.name} bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 42,
    slots: [
      slot("vegetable", "Mixed vegetables", true, "tomato", [
        option("tomato", 300, "g", "halved", 3, undefined, {
          tr: { preparation: "ikiye kesilmiş" },
        }),
        option("bell-pepper", 300, "g", "cut into chunks", 2, undefined, {
          tr: { preparation: "parçalar halinde kesilmiş" },
        }),
      ]),
      slot("extra-veg", "Extra vegetable", true, "zucchini", [
        option("zucchini", 300, "g", "cut into thick rounds", 3, undefined, {
          tr: { preparation: "kalın halkalar halinde kesilmiş" },
        }),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 2, undefined, {
          tr: { preparation: "2 cm'lik küpler halinde doğranmış" },
        }),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("lentils", 200, "g", "cooked", 1, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("richness", "Richness", false, "olives", [
        option("olives", 80, "g", "pitted", 3, undefined, {
          tr: { preparation: "çekirdeksiz" },
        }),
        option("feta", 80, "g", "crumbled", 2, undefined, {
          tr: { preparation: "ufalanmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 4, "piece", "left whole", 1, undefined, {
          tr: { preparation: "bütün" },
        }),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      slot("herb", "Fresh herb", false, "basil", [
        option("basil", 15, "g", "torn", 3, undefined, {
          tr: { preparation: "parçalanmış" },
        }),
        option("parsley", 15, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C.",
        translations: { tr: { template: "Fırını 200°C'ye ısıtın." } },
      },
      {
        id: "toss-veg",
        template:
          "Combine {vegetable.name}, {extra-veg.name}, {protein.name}, {garlic.name}, {oil.name}, {salt.name}, and {black-pepper.name} in a large roasting dish. Toss well.",
        translations: {
          tr: {
            template:
              "Büyük bir fırın kabında {vegetable.name}, {extra-veg.name}, {protein.name}, {garlic.name}, {oil.name}, {salt.name} ve {black-pepper.name} birleştirin. İyice karıştırın.",
          },
        },
      },
      {
        id: "roast",
        template:
          "Roast for 30 minutes until the vegetables are tender and beginning to caramelize at the edges.",
        translations: {
          tr: {
            template:
              "Sebzeler yumuşayıp kenarları karamelleşmeye başlayana kadar 30 dakika kızartın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Add {richness.name} if using and return to the oven for 5 minutes. Scatter {herb.name} over the top and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {richness.name} ekleyin ve 5 dakika daha fırına geri koyun. Üzerine {herb.name} serpin ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "fat", "umami", "fresh", "acid"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Akdeniz usulü {vegetable.name} & {protein.name} fırın yemeği",
      },
    },
  },
  {
    id: "lemon-herb-potato-tray-bake",
    nameTemplate: "Crispy {base.name} & {veg.name} tray bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 50,
    slots: [
      slot("base", "Starchy base", true, "potato", [
        option("potato", 500, "g", "cut into 3 cm chunks", 3, undefined, {
          tr: { preparation: "3 cm'lik parçalar halinde kesilmiş" },
        }),
        option("sweet-potato", 500, "g", "cut into 3 cm chunks", 2, undefined, {
          tr: { preparation: "3 cm'lik parçalar halinde kesilmiş" },
        }),
      ]),
      slot("veg", "Vegetable", true, "green-beans", [
        option("green-beans", 250, "g", "trimmed", 3, undefined, {
          tr: { preparation: "uçları kesilmiş" },
        }),
        option("broccoli", 300, "g", "cut into florets", 2, undefined, {
          tr: { preparation: "demetlere ayrılmış" },
        }),
      ]),
      slot("protein", "Protein", false, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("lentils", 200, "g", "cooked", 2, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      slot("acid", "Bright acid", true, "lemon", [
        option("lemon", 1, "piece", "zested and juiced", 3, undefined, {
          tr: { preparation: "kabuğu rendelenmiş ve suyu çıkarılmış" },
        }),
        option("balsamic-vinegar", 2, "tbsp", undefined, 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 4, "piece", "left whole", 1, undefined, {
          tr: { preparation: "bütün" },
        }),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3, undefined, {
          tr: { preparation: "doğranmış" },
        }),
        option("dill", 15, "g", "chopped", 2, undefined, {
          tr: { preparation: "doğranmış" },
        }),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 210°C.",
        translations: { tr: { template: "Fırını 210°C'ye ısıtın." } },
      },
      {
        id: "roast-base",
        template:
          "Toss {base.name} and {garlic.name} with {oil.name}, {salt.name}, and {black-pepper.name}. Spread on a large baking sheet. Roast for 25 minutes.",
        translations: {
          tr: {
            template:
              "{base.name} ve {garlic.name} üzerine {oil.name}, {salt.name} ve {black-pepper.name} gezdirin. Büyük bir tepsiye yayın. 25 dakika kızartın.",
          },
        },
      },
      {
        id: "add-veg-protein",
        template:
          "Add {veg.name} and {protein.name} if using to the sheet. Toss everything together and roast for a further 15 minutes until {base.name} is crispy at the edges.",
        translations: {
          tr: {
            template:
              "Tepsiye {veg.name} ve kullanıyorsanız {protein.name} ekleyin. Her şeyi karıştırın ve {base.name} kenarları çıtır olana kadar 15 dakika daha kızartın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Squeeze {acid.name} over everything, scatter {herb.name} if using, and serve hot.",
        translations: {
          tr: {
            template:
              "Her şeyin üzerine {acid.name} sıkın, kullanıyorsanız {herb.name} serpin ve sıcak servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "fat", "acid", "fresh", "crunch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Çıtır {base.name} & {veg.name} fırın yemeği",
      },
    },
  },
  {
    id: "chickpea-vegetable-tray-bake",
    nameTemplate: "{protein.name} & {vegetable.name} tray bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 40,
    slots: [
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and dried", 3),
        option("black-beans", 240, "g", "drained and dried", 1),
      ]),
      slot("vegetable", "Vegetable", true, "zucchini", [
        option("zucchini", 350, "g", "cut into thick half-moons", 3),
        option("bell-pepper", 350, "g", "cut into wide strips", 2),
        option("mushroom", 350, "g", "halved", 1),
      ]),
      slot("base", "Base", true, "sweet-potato", [
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 3),
        option("potato", 400, "g", "cut into 2 cm cubes", 2),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Spice", true, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "lemon", [
        option("lemon", 1, "piece", "cut into wedges", 3),
        option("tahini", 2, "tbsp", undefined, 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C and line a large tray.",
        translations: {
          tr: {
            template:
              "Fırını 220°C'ye ısıtın ve büyük bir tepsiye yağlı kağıt serin.",
          },
        },
      },
      {
        id: "season",
        template:
          "Toss {base.name}, {vegetable.name}, and {protein.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{base.name}, {vegetable.name} ve {protein.name} üzerine {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} gezdirin ve karıştırın.",
          },
        },
      },
      {
        id: "roast",
        template:
          "Spread into one layer and roast for 25 to 30 minutes, turning once, until browned and tender.",
        translations: {
          tr: {
            template:
              "Tek katman halinde yayın ve bir kez çevirerek 25-30 dakika kızarıp yumuşayana kadar fırınlayın.",
          },
        },
      },
      {
        id: "finish",
        template: "Serve with {finish.name} if using.",
        translations: {
          tr: { template: "Kullanıyorsanız {finish.name} ile servis edin." },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "fat", "acid", "aroma"],
    editorialPriority: 10,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {vegetable.name} tepsi yemeği",
      },
    },
  },
  {
    id: "miso-tofu-broccoli-tray",
    nameTemplate: "Miso {protein.name} & {vegetable.name} tray",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 38,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained and dried", 1),
      ]),
      slot("vegetable", "Vegetable", true, "broccoli", [
        option("broccoli", 400, "g", "cut into florets", 3),
        option("mushroom", 400, "g", "halved", 2),
      ]),
      slot("glaze", "Umami glaze", true, "miso", [option("miso", 1, "tbsp")]),
      slot("aromatic", "Aromatic", true, "ginger", [
        option("ginger", 20, "g", "finely grated", 3),
        option("garlic", 2, "piece", "finely grated", 2),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("finish", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp", undefined, 3),
        option("sesame-seeds", 1, "tbsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      pantry.water(30),
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 215°C and line a large tray.",
        translations: {
          tr: {
            template:
              "Fırını 215°C'ye ısıtın ve büyük bir tepsiye yağlı kağıt serin.",
          },
        },
      },
      {
        id: "make-glaze",
        template:
          "Whisk {glaze.name}, {aromatic.name}, {oil.name}, {water.name}, and {black-pepper.name} into a loose glaze.",
        translations: {
          tr: {
            template:
              "{glaze.name}, {aromatic.name}, {oil.name}, {water.name} ve {black-pepper.name} akışkan bir sır elde edene kadar çırpın.",
          },
        },
      },
      {
        id: "coat",
        template:
          "Coat {protein.name} and {vegetable.name} in the glaze, then spread them in one layer.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {vegetable.name} sıra içinde bulayın, ardından tek katman halinde yayın.",
          },
        },
      },
      {
        id: "roast",
        template:
          "Roast for 22 to 25 minutes, turning once, until browned at the edges.",
        translations: {
          tr: {
            template:
              "Bir kez çevirerek 22-25 dakika kenarları kızarana kadar fırınlayın.",
          },
        },
      },
      {
        id: "finish",
        template: "Scatter over {finish.name} if using and serve.",
        translations: {
          tr: {
            template: "Kullanıyorsanız {finish.name} serpin ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "fat", "fresh", "crunch"],
    editorialPriority: 9,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "Misolu {protein.name} ve {vegetable.name} tepsi",
      },
    },
  },
  {
    id: "eggplant-lentil-bake",
    nameTemplate: "{vegetable.name} & {protein.name} bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 48,
    slots: [
      slot("vegetable", "Vegetable", true, "eggplant", [
        option("eggplant", 500, "g", "cut into 2 cm cubes", 3),
        option("zucchini", 500, "g", "cut into thick half-moons", 1),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 300, "g", "cooked and drained", 3),
        option("black-beans", 240, "g", "drained", 1),
      ]),
      slot("tomato", "Sauce", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("aromatic", "Aromatic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 3),
        option("onion", 1, "piece", "finely diced", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("top", "Topping", false, "feta", [
        option("feta", 80, "g", "crumbled", 3),
        option("bread", 80, "g", "torn into crumbs", 2),
        option("pumpkin-seeds", 3, "tbsp", undefined, 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 210°C.",
        translations: { tr: { template: "Fırını 210°C'ye ısıtın." } },
      },
      {
        id: "season-vegetables",
        template:
          "Toss {vegetable.name} and {aromatic.name} with {oil.name}, {salt.name}, and {black-pepper.name} in a baking dish.",
        translations: {
          tr: {
            template:
              "Bir fırın kabında {vegetable.name} ve {aromatic.name} üzerine {oil.name}, {salt.name} ve {black-pepper.name} gezdirin ve karıştırın.",
          },
        },
      },
      {
        id: "first-bake",
        template: "Bake for 20 minutes, stirring once.",
        translations: {
          tr: { template: "Bir kez karıştırarak 20 dakika pişirin." },
        },
      },
      {
        id: "add-sauce",
        template:
          "Fold in {protein.name} and {tomato.name}. Add {top.name} if using.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {tomato.name} ekleyip nazikçe karıştırın. Kullanıyorsanız {top.name} ekleyin.",
          },
        },
      },
      {
        id: "finish-bake",
        template:
          "Bake for another 15 minutes, until bubbling and browned at the edges.",
        translations: {
          tr: {
            template:
              "Kabarcıklanıp kenarları kızarana kadar 15 dakika daha pişirin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "protein", "fat", "acid", "umami"],
    editorialPriority: 8,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "Fırında {vegetable.name} ve {protein.name}",
      },
    },
  },
  {
    id: "tomato-tofu-tray-bake",
    nameTemplate: "Oven-baked {protein.name} & {sauce.name} tray",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 38,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed"),
      ]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("vegetable", "Vegetable", true, "zucchini", [
        option("zucchini", 300, "g", "halved lengthways and sliced", 3),
        option("bell-pepper", 300, "g", "cut into chunks", 2),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "thinly sliced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Warm spice", true, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C.",
        translations: { tr: { template: "Fırını 200°C'ye ısıtın." } },
      },
      {
        id: "combine",
        template:
          "In a large baking dish, mix {sauce.name}, {onion.name}, {garlic.name}, {vegetable.name}, {spice.name}, {oil.name}, {salt.name}, and {black-pepper.name}. Nestle {protein.name} pieces into the sauce.",
        translations: {
          tr: {
            template:
              "Büyük bir fırın kabında {sauce.name}, {onion.name}, {garlic.name}, {vegetable.name}, {spice.name}, {oil.name}, {salt.name} ve {black-pepper.name} karıştırın. {protein.name} parçalarını sosun içine yerleştirin.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Bake uncovered for 26 minutes until the {protein.name} is golden at the edges and the sauce is bubbling.",
        translations: {
          tr: {
            template:
              "Kapaksız olarak {protein.name} kenarları kızarana ve sos kabarcıklanana kadar 26 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {herb.name} if using and serve with rice or flatbread.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {herb.name} serpin ve pirinç ya da lavaşla servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "umami", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Fırınlanmış {protein.name} ve {sauce.name} tepsi",
      },
    },
  },
  {
    id: "cauliflower-white-bean-tray-bake",
    nameTemplate: "Roasted {vegetable.name} & {protein.name} tray bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 38,
    slots: [
      slot("vegetable", "Main vegetable", true, "cauliflower", [
        option("cauliflower", 500, "g", "cut into florets", 3),
        option("broccoli", 450, "g", "cut into florets", 2),
      ]),
      slot("protein", "Protein", true, "white-beans", [
        option("white-beans", 240, "g", "drained and rinsed", 3),
        option("chickpeas", 240, "g", "drained and dried", 2),
        option("lentils", 200, "g", "cooked", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1.5, "tsp", undefined, 3),
        option("smoked-paprika", 1.5, "tsp", undefined, 2),
      ]),
      slot("acid", "Acid finish", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 1, "tbsp", undefined, 1),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "roughly chopped", 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 210°C.",
        translations: { tr: { template: "Fırını 210°C'ye ısıtın." } },
      },
      {
        id: "toss",
        template:
          "On a large baking sheet, toss {vegetable.name} with {oil.name}, {garlic.name}, {spice.name}, {salt.name}, and {black-pepper.name}. Spread in a single layer.",
        translations: {
          tr: {
            template:
              "Büyük bir tepsiye {vegetable.name} ile {oil.name}, {garlic.name}, {spice.name}, {salt.name} ve {black-pepper.name} karıştırın. Tek katman halinde yayın.",
          },
        },
      },
      {
        id: "roast",
        template:
          "Roast for 20 minutes until the {vegetable.name} is golden at the edges.",
        translations: {
          tr: {
            template:
              "{vegetable.name} kenarları kızarana kadar 20 dakika kızartın.",
          },
        },
      },
      {
        id: "add-beans",
        template:
          "Scatter {protein.name} over the tray. Return to the oven for 8 more minutes until the beans are lightly crisped.",
        translations: {
          tr: {
            template:
              "Tepsiye {protein.name} serpin. Fasulye hafifçe çıtırlanana kadar 8 dakika daha fırında pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Drizzle with {acid.name} and scatter {herb.name} if using before serving.",
        translations: {
          tr: {
            template:
              "Servis etmeden önce üzerine {acid.name} gezdirin ve kullanıyorsanız {herb.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "fat", "acid", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Fırınlanmış {vegetable.name} ve {protein.name} tepsi yemeği",
      },
    },
  },
  {
    id: "mushroom-lentil-baked-casserole",
    nameTemplate: "Baked {protein.name} & {mushroom.name} casserole",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 3,
    activeMinutes: 15,
    totalMinutes: 50,
    slots: [
      slot("protein", "Lentils", true, "lentils", [
        option("lentils", 300, "g", "rinsed"),
      ]),
      slot("mushroom", "Mushroom", true, "mushroom", [
        option("mushroom", 350, "g", "thickly sliced", 3),
        option("eggplant", 400, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("vegetable", "Extra vegetable", true, "carrot", [
        option("carrot", 200, "g", "diced", 3),
        option("celery", 150, "g", "diced", 2),
        option("zucchini", 200, "g", "diced", 1),
      ]),
      slot("onion", "Aromatic base", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("sauce", "Liquid base", true, "tomato", [
        option("tomato", 400, "g", "crushed", 3),
        option("miso", 1, "tbsp", undefined, 2),
        option("soy-sauce", 1, "tbsp", undefined, 1),
      ]),
      slot("spice", "Warm spice", true, "smoked-paprika", [
        option("smoked-paprika", 1.5, "tsp", undefined, 3),
        option("cumin", 1.5, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("herb", "Herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped"),
      ]),
      pantry.water(500),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 190°C.",
        translations: { tr: { template: "Fırını 190°C'ye ısıtın." } },
      },
      {
        id: "saute",
        template:
          "Heat {oil.name} in a flameproof casserole or Dutch oven over medium heat. Cook {onion.name} and {vegetable.name} for 5 minutes until soft. Add {garlic.name} and {mushroom.name} and cook for 3 more minutes.",
        translations: {
          tr: {
            template:
              "Ocağa dayanıklı bir güveç ya da dökme demir tencerede {oil.name} orta ateşte ısıtın. {onion.name} ve {vegetable.name} yumuşayana kadar 5 dakika kavurun. {garlic.name} ve {mushroom.name} ekleyip 3 dakika daha pişirin.",
          },
        },
      },
      {
        id: "build",
        template:
          "Stir in {sauce.name}, {spice.name}, {protein.name}, and {water.name}. Season with {salt.name} and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {spice.name}, {protein.name} ve {water.name} ekleyip karıştırın. {salt.name} ve {black-pepper.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Cover and bake for 30 minutes until the {protein.name} is tender and has absorbed most of the liquid.",
        translations: {
          tr: {
            template:
              "Kapağını kapatın ve {protein.name} yumuşayıp sıvının çoğunu çekene kadar 30 dakika fırınlayın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {herb.name} if using and serve straight from the pot.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {herb.name} serpin ve doğrudan tencereden servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "fat", "aroma", "heat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Fırınlanmış {protein.name} ve {mushroom.name} güveci",
      },
    },
  },
  {
    id: "sweet-potato-black-bean-tray-bake",
    nameTemplate: "{vegetable.name} & {protein.name} tray bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 45,
    slots: [
      slot("vegetable", "Main vegetable", true, "sweet-potato", [
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 3),
        option("potato", 400, "g", "cut into 2 cm cubes", 2),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "cut into wedges"),
      ]),
      slot("spice", "Spice blend", true, "smoked-paprika", [
        option("smoked-paprika", 1.5, "tsp", undefined, 3),
        option("cumin", 1.5, "tsp", undefined, 2),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 0.5, "tsp"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      slot("acid", "Bright finish", true, "lime", [
        option("lime", 1, "piece", "juiced", 3),
        option("lemon", 1, "piece", "juiced", 2),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("parsley", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 210°C.",
        translations: { tr: { template: "Fırını 210°C'ye ısıtın." } },
      },
      {
        id: "season",
        template:
          "On a large baking tray, toss {vegetable.name}, {protein.name}, {onion.name}, {spice.name}, {heat.name} if using, and {oil.name}. Season with {salt.name} and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "Büyük bir tepsiye {vegetable.name}, {protein.name}, {onion.name}, {spice.name}, kullanıyorsanız {heat.name} ve {oil.name} koyun. {salt.name} ve {black-pepper.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "roast",
        template:
          "Spread in a single layer and roast for 30 to 35 minutes, turning once, until {vegetable.name} is tender and caramelised at the edges.",
        translations: {
          tr: {
            template:
              "Tek katman halinde yayın ve bir kez çevirerek {vegetable.name} yumuşayıp kenarları karamelleşene kadar 30-35 dakika kızartın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Squeeze over {acid.name}, scatter {herb.name} if using, and serve.",
        translations: {
          tr: {
            template:
              "Üzerine {acid.name} sıkın, kullanıyorsanız {herb.name} serpin ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "heat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{vegetable.name} ve {protein.name} tepsi yemeği",
      },
    },
  },
  {
    id: "roasted-pepper-lentil-bake",
    nameTemplate: "{vegetable.name} & {protein.name} Mediterranean bake",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 50,
    slots: [
      slot("vegetable", "Main vegetable", true, "bell-pepper", [
        option("bell-pepper", 3, "piece", "deseeded and cut into strips", 3),
        option("zucchini", 350, "g", "cut into thick rounds", 2),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 160, "g", "rinsed", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 300, "g", "roughly chopped"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("spice", "Spice", true, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("basil", 10, "g", "torn", 2),
      ]),
      slot("acid", "Bright finish", false, "balsamic-vinegar", [
        option("balsamic-vinegar", 1, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 2),
      ]),
      pantry.water(150),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C.",
        translations: { tr: { template: "Fırını 200°C'ye ısıtın." } },
      },
      {
        id: "combine",
        template:
          "In a baking dish, combine {vegetable.name}, {protein.name}, {tomato.name}, {garlic.name}, {spice.name}, {oil.name}, {water.name}, {salt.name}, and {black-pepper.name}. Stir well.",
        translations: {
          tr: {
            template:
              "Bir fırın kabında {vegetable.name}, {protein.name}, {tomato.name}, {garlic.name}, {spice.name}, {oil.name}, {water.name}, {salt.name} ve {black-pepper.name} birleştirin. İyice karıştırın.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Cover tightly with foil and bake for 30 minutes. Remove foil and bake for a further 10 minutes until the top is slightly caramelised.",
        translations: {
          tr: {
            template:
              "Üzerini alüminyum folyo ile sıkıca kapatın ve 30 dakika pişirin. Folyoyu kaldırın ve üzeri hafifçe karamelleşene kadar 10 dakika daha pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Drizzle over {acid.name} if using, scatter {herb.name} if using, and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {acid.name} gezdirin, kullanıyorsanız {herb.name} serpin ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "fat", "acid", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Akdeniz usulü {vegetable.name} ve {protein.name} fırın yemeği",
      },
    },
  },
  {
    id: "turkish-eggplant-chickpea-tray",
    nameTemplate: "Turkish-spiced {vegetable.name} & {protein.name} tray",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 40,
    slots: [
      slot("vegetable", "Main vegetable", true, "eggplant", [
        option("eggplant", 500, "g", "cut into 3 cm cubes", 3),
        option("zucchini", 500, "g", "cut into thick half-moons", 2),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and dried", 3),
        option("white-beans", 240, "g", "drained and dried", 2),
      ]),
      slot("sauce", "Spiced paste", true, "red-pepper-paste", [
        option("red-pepper-paste", 2, "tbsp", undefined, 3),
        option("tomato-paste", 2, "tbsp", undefined, 2),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 300, "g", "roughly chopped", 3),
        option("bell-pepper", 2, "piece", "deseeded and roughly chopped", 2),
      ]),
      slot("spice", "Warm spice", true, "allspice", [
        option("allspice", 1, "tsp", undefined, 3),
        option("cinnamon", 0.5, "tsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "sumac", [
        option("sumac", 1, "tsp", undefined, 3),
        option("pomegranate-molasses", 1, "tbsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C and line a large baking tray.",
        translations: {
          tr: {
            template:
              "Fırını 200°C'ye ısıtın ve büyük bir tepsiye yağlı kağıt serin.",
          },
        },
      },
      {
        id: "season",
        template:
          "In a large bowl, toss {vegetable.name} and {protein.name} with {oil.name}, {sauce.name}, {spice.name}, {salt.name}, and {black-pepper.name} until evenly coated.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {vegetable.name} ve {protein.name} üzerine {oil.name}, {sauce.name}, {spice.name}, {salt.name} ve {black-pepper.name} gezdirin; eşit şekilde kaplanana kadar karıştırın.",
          },
        },
      },
      {
        id: "roast",
        template:
          "Spread onto the tray in one layer. Roast for 20 minutes. Add {tomato.name}, stir gently, and roast for 12 to 15 minutes more until the {vegetable.name} is golden and the tomatoes have collapsed.",
        translations: {
          tr: {
            template:
              "Tepsiye tek katman halinde yayın. 20 dakika kızartın. {tomato.name} ekleyip nazikçe karıştırın ve {vegetable.name} kızarıp domatesler çöküne kadar 12-15 dakika daha kızartın.",
          },
        },
      },
      {
        id: "finish",
        template: "Drizzle or sprinkle over {finish.name} if using, and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} gezdirin ya da serpin ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "fat", "aroma", "acid", "protein"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Türk baharatlı {vegetable.name} ve {protein.name} tepsi",
      },
    },
  },
  {
    id: "stuffed-grape-leaves",
    nameTemplate: "Stuffed grape leaves",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 35,
    totalMinutes: 60,
    slots: [
      slot("leaves", "Grape leaves", true, "grape-leaves", [
        option("grape-leaves", 20, "piece", "rinsed"),
      ]),
      slot("grain", "Grain filling", true, "rice", [
        option("rice", 200, "g", "uncooked", 3),
        option("bulgur", 180, "g", "uncooked", 2),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely chopped", 3),
        option("red-onion", 1, "piece", "finely chopped", 2),
      ]),
      slot("herb", "Herb", true, "parsley", [
        option("parsley", 20, "g", "finely chopped", 3),
        option("dill", 15, "g", "finely chopped", 2),
      ]),
      slot("spice", "Warm spice", true, "allspice", [
        option("allspice", 1, "tsp", undefined, 3),
        option("cinnamon", 1, "tsp", undefined, 2),
        option("cumin", 1, "tsp", undefined, 1),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
      pantry.water(400),
    ],
    steps: [
      {
        id: "make-filling",
        template:
          "Mix {grain.name}, {onion.name}, {herb.name}, {spice.name}, half the {acid.name}, half the {oil.name}, {salt.name}, and {black-pepper.name} together in a bowl.",
        translations: {
          tr: {
            template:
              "Bir kasede {grain.name}, {onion.name}, {herb.name}, {spice.name}, {acid.name} ve {oil.name} uygulamasının yarısını, {salt.name} ve {black-pepper.name} karıştırın.",
          },
        },
      },
      {
        id: "roll",
        template:
          "Place a heaped teaspoon of filling near the base of each {leaves.name} leaf. Fold in the sides and roll up tightly.",
        translations: {
          tr: {
            template:
              "Her {leaves.name} yaprağının tabanına tepeli bir çay kaşığı dolgu koyun. Yanlarını içe katlayıp sıkıca sarın.",
          },
        },
      },
      {
        id: "layer",
        template:
          "Drizzle the remaining {oil.name} in the base of a wide pot. Pack the rolls in seam-side down in a single layer.",
        translations: {
          tr: {
            template:
              "Geniş bir tencerenin dibine kalan {oil.name} gezdirin. Sarmaları dikişleri aşağı gelecek şekilde tek sıra halinde yerleştirin.",
          },
        },
      },
      {
        id: "cook",
        template:
          "Pour {water.name} and the remaining {acid.name} over the rolls. Place a heatproof plate on top to keep them submerged. Cover and simmer on the lowest heat for 40 minutes until the grain is cooked through.",
        translations: {
          tr: {
            template:
              "Sarmaların üzerine {water.name} ve kalan {acid.name} dökün. Üzerlerini bastırmak için ısıya dayanıklı bir tabak koyun. Kapağını kapatın ve en kısık ateşte tahıl iyice pişene kadar 40 dakika hafifçe kaynatın.",
          },
        },
      },
      {
        id: "serve",
        template: "Serve warm or at room temperature.",
        translations: {
          tr: { template: "Ilık veya oda sıcaklığında servis edin." },
        },
      },
    ],
    targetBalanceRoles: ["starch", "aroma", "acid", "fat", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Asma yaprağı sarması",
      },
    },
  },
  {
    id: "frittata",
    nameTemplate: "{vegetable.name} & {richness.name} frittata",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 3,
    activeMinutes: 15,
    totalMinutes: 30,
    slots: [
      slot("egg", "Eggs", true, "egg", [
        option("egg", 5, "piece", undefined, 1, {
          safetyCue:
            "bake for 10 to 12 minutes until the centre is set and the top is lightly golden",
        }),
      ]),
      slot("richness", "Creamy element", true, "feta", [
        option("feta", 80, "g", "crumbled", 3),
        option("yogurt", 3, "tbsp", undefined, 2),
      ]),
      slot("vegetable", "Vegetable", true, "spinach", [
        option("spinach", 80, "g", "roughly chopped", 3),
        option("zucchini", 1, "piece", "thinly sliced", 2),
        option("bell-pepper", 1, "piece", "diced", 1),
      ]),
      slot("allium", "Allium", true, "scallion", [
        option("scallion", 3, "piece", "sliced", 3),
        option("leek", 1, "piece", "thinly sliced", 2),
        option("onion", 1, "piece", "finely diced", 1),
      ]),
      slot("herb", "Herb", false, "dill", [
        option("dill", 10, "g", "roughly chopped", 3),
        option("parsley", 10, "g", "roughly chopped", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "preheat",
        template:
          "Preheat the oven to 190 °C. Crack {egg.name} into a bowl, season with {salt.name} and {black-pepper.name}, and whisk well.",
        translations: {
          tr: {
            template:
              "Fırını 190°C'ye ısıtın. {egg.name} bir kaseye kırın, {salt.name} ve {black-pepper.name} ile tatlandırın ve iyice çırpın.",
          },
        },
      },
      {
        id: "saute",
        template:
          "Heat {oil.name} in an oven-safe frying pan over medium heat. Cook {allium.name} for 3 minutes until soft, then add {vegetable.name} and stir for 2 minutes.",
        translations: {
          tr: {
            template:
              "{oil.name} orta ateşte fırına dayanıklı bir tavada ısıtın. {allium.name} 3 dakika yumuşayana kadar pişirin, ardından {vegetable.name} ekleyip 2 dakika daha karıştırın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Scatter {richness.name} and {herb.name} if using over the vegetables, then pour in the egg mixture. Cook on the stovetop for 2 minutes until the edges just begin to set.",
        translations: {
          tr: {
            template:
              "Sebzelerin üzerine {richness.name} ve varsa {herb.name} serpin, ardından yumurta karışımını dökün. Kenarlar tutmaya başlayana kadar ocakta 2 dakika pişirin.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Transfer the pan to the oven and {egg.safetyCue}. Rest for 2 minutes before slicing.",
        translations: {
          tr: {
            template:
              "Tavayı fırına alın ve {egg.safetyCue}. Dilimlemeden önce 2 dakika dinlendirin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "fresh", "aroma", "salt"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: { nameTemplate: "{vegetable.name} ve {richness.name} frittata" },
    },
  },
  {
    id: "stuffed-bell-peppers",
    nameTemplate: "Baked stuffed peppers with {grain.name} & {protein.name}",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 50,
    slots: [
      slot("vessel", "Bell peppers", true, "bell-pepper", [
        option("bell-pepper", 4, "piece", "halved lengthways and deseeded"),
      ]),
      slot("grain", "Grain", true, "rice", [
        option("rice", 180, "g", "cooked", 3),
        option("quinoa", 180, "g", "cooked", 2),
        option("couscous", 150, "g", "cooked", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 200, "g", "drained", 3),
        option("black-beans", 200, "g", "drained", 2),
        option("lentils", 200, "g", "cooked", 1),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 200, "g", "diced", 3),
        option("tomato-paste", 2, "tbsp", undefined, 2),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 3),
        option("red-onion", 1, "piece", "finely diced", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
        option("allspice", 1, "tsp", undefined, 1),
      ]),
      slot("herb", "Herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("dill", 15, "g", "roughly chopped", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "preheat",
        template:
          "Preheat the oven to 200 °C. Arrange {vessel.name} cut-side up in a baking dish, brush with 1 tbsp of {oil.name}, season with {salt.name} and {black-pepper.name}, and roast for 10 minutes.",
        translations: {
          tr: {
            template:
              "Fırını 200°C'ye ısıtın. {vessel.name} kesilmiş yüzleri yukarı bakacak şekilde bir fırın kabına dizin, 1 yemek kaşığı {oil.name} ile fırçalayın, {salt.name} ve {black-pepper.name} ile tatlandırın ve 10 dakika pişirin.",
          },
        },
      },
      {
        id: "filling",
        template:
          "While the peppers roast, heat the remaining {oil.name} in a pan. Fry {onion.name} for 4 minutes, then add {garlic.name} and {spice.name} and cook 1 minute more. Stir in {tomato.name}, {grain.name}, and {protein.name}. Season well.",
        translations: {
          tr: {
            template:
              "Biberler fırında pişerken kalan {oil.name} bir tavada ısıtın. {onion.name} 4 dakika kavurun, ardından {garlic.name} ve {spice.name} ekleyip 1 dakika daha pişirin. {tomato.name}, {grain.name} ve {protein.name} ekleyip karıştırın. Bol tatlandırın.",
          },
        },
      },
      {
        id: "stuff-and-bake",
        template:
          "Spoon the filling into the roasted {vessel.name}, packing it in. Return to the oven and bake for 20–25 minutes until the peppers are tender and the filling is lightly golden on top.",
        translations: {
          tr: {
            template:
              "Fırında pişmiş {vessel.name} içine dolguyu doldurun, sıkıştırarak koyun. Fırına geri alın ve biberler yumuşayana ve dolgu hafifçe altın rengi alana kadar 20-25 dakika daha pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Scatter {herb.name} if using over the top and serve straight from the dish.",
        translations: {
          tr: {
            template:
              "Varsa {herb.name} üzerine serpin ve doğrudan kaptan servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "aroma", "bulk", "fat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{grain.name} ve {protein.name} ile fırında dolma biber",
      },
    },
  },
  {
    id: "baked-falafel",
    nameTemplate: "Baked {legume.name} falafel with {sauce.name}",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "oven",
    servings: 3,
    activeMinutes: 20,
    totalMinutes: 40,
    slots: [
      slot("legume", "Legume", true, "chickpeas", [
        option("chickpeas", 400, "g", "drained and rinsed", 3),
        option("black-beans", 400, "g", "drained and rinsed", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "roughly chopped"),
      ]),
      slot("onion", "Onion", false, "red-onion", [
        option("red-onion", 0.5, "piece", "roughly chopped", 3),
        option("onion", 0.5, "piece", "roughly chopped", 2),
      ]),
      slot("herb", "Herb", true, "parsley", [
        option("parsley", 20, "g", "roughly chopped", 3),
        option("cilantro", 20, "g", "roughly chopped", 2),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      slot("binder", "Binder", true, "chickpea-flour", [
        option("chickpea-flour", 2, "tbsp"),
      ]),
      slot("sesame", "Sesame", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp"),
      ]),
      slot("sauce", "Sauce", false, "tahini", [
        option("tahini", 3, "tbsp", undefined, 3),
        option("sriracha", 1, "tbsp", undefined, 2),
        option("yogurt", 3, "tbsp", undefined, 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template:
          "Heat the oven to 200°C. Line a baking tray with parchment and brush with {oil.name}.",
        translations: {
          tr: {
            template:
              "Fırını 200°C'ye ısıtın. Bir fırın tepsisini yağlı kağıt ile kaplayın ve {oil.name} ile fırçalayın.",
          },
        },
      },
      {
        id: "blend",
        template:
          "Pulse {legume.name}, {garlic.name}, {onion.name} if using, {herb.name}, {spice.name}, {binder.name}, {salt.name}, and {black-pepper.name} in a food processor until the mixture is finely chopped and just holds together when pressed — do not over-blend to a paste.",
        translations: {
          tr: {
            template:
              "{legume.name}, {garlic.name}, varsa {onion.name}, {herb.name}, {spice.name}, {binder.name}, {salt.name} ve {black-pepper.name} mutfak robotuna koyun ve karışım ince kıyılıncaya ve bastırıldığında birbirine yapışacak kıvama gelene kadar pulsla çekin — hamur kıvamına getirmeyin.",
          },
        },
      },
      {
        id: "shape",
        template:
          "Roll heaped tablespoons of the mixture into balls or flatten into patties. Scatter {sesame.name} on top if using. Arrange on the prepared tray and drizzle with remaining {oil.name}.",
        translations: {
          tr: {
            template:
              "Karışımdan dolu yemek kaşığı parçalar alıp top şeklinde yuvarlayın veya yassıltın. Varsa üzerine {sesame.name} serpin. Hazırlanan tepside dizin ve kalan {oil.name} ile gezdirin.",
          },
        },
      },
      {
        id: "bake-serve",
        template:
          "Bake 25–28 minutes, turning halfway, until deep golden and crisp on the outside. Serve warm with {sauce.name} if using.",
        translations: {
          tr: {
            template:
              "Yarıda bir çevirerek 25-28 dakika, dışı koyu altın rengi ve çıtır olana kadar pişirin. Varsa {sauce.name} ile sıcak servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "aroma", "fat", "crunch"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Fırında {legume.name} falafeli ve {sauce.name}",
      },
    },
  },
  {
    id: "spanish-tortilla",
    nameTemplate: "Spanish tortilla with {potato.name} & {allium.name}",
    foodType: "tray-bake-casserole",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 30,
    totalMinutes: 45,
    slots: [
      slot("potato", "Potato", true, "potato", [
        option("potato", 500, "g", "peeled and sliced into 3 mm rounds"),
        option(
          "sweet-potato",
          500,
          "g",
          "peeled and sliced into 3 mm rounds",
          2,
        ),
      ]),
      slot("egg", "Eggs", true, "egg", [
        option("egg", 6, "piece", undefined, 1, {
          safetyCue:
            "cook a further 3 to 4 minutes until just set through with a very slight give in the centre",
        }),
      ]),
      slot("allium", "Allium", true, "onion", [
        option("onion", 1, "piece", "halved and thinly sliced", 3),
        option("leek", 2, "piece", "white part only, thinly sliced", 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 120, "ml")]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "confit",
        template:
          "Heat {oil.name} in a 24 cm non-stick pan over medium-low heat. Add {potato.name} and {allium.name} — the slices should be mostly submerged in oil. Cook gently, stirring occasionally, for 15–18 minutes until the {potato.name} is completely tender and slightly translucent but not browned. Drain through a colander set over a bowl, reserving the flavoured oil. Season the drained potato generously with {salt.name}.",
        translations: {
          tr: {
            template:
              "24 cm yapışmaz bir tavada {oil.name} kısık-orta ateşte ısıtın. {potato.name} ve {allium.name} ekleyin — dilimler çoğunlukla yağa batmış olmalı. Zaman zaman karıştırarak, {potato.name} tamamen yumuşayana ve hafifçe yarı saydam görünene ama kızarmamış hale gelene kadar 15-18 dakika nazikçe pişirin. Aromalı yağı bir kapta saklayarak kevgirle süzün. Süzülen patatesi {salt.name} ile cömertçe tatlandırın.",
          },
        },
      },
      {
        id: "beat-eggs",
        template:
          "Beat {egg.name} in a large bowl with {salt.name} and {black-pepper.name} until uniform. Add the drained {potato.name} and {allium.name} and fold gently to combine. Leave to rest for 5 minutes — the potato absorbs some egg and helps the tortilla hold together.",
        translations: {
          tr: {
            template:
              "{egg.name} büyük bir kasede {salt.name} ve {black-pepper.name} ile homojen olana kadar çırpın. Süzülmüş {potato.name} ve {allium.name} ekleyip nazikçe katlayın. 5 dakika dinlenmeye bırakın — patates bir miktar yumurtayı emerek tortillanın tutmasına yardımcı olur.",
          },
        },
      },
      {
        id: "cook-first-side",
        template:
          "Return 2 tbsp of the reserved oil to the pan over medium heat. Pour in the {egg.name}–{potato.name} mixture and press to an even thickness. Cook undisturbed for 4–5 minutes until the edges are fully set and the centre is mostly firm but still slightly wobbly when you shake the pan.",
        translations: {
          tr: {
            template:
              "Rezerve yağdan 2 yemek kaşığını tavaya orta ateşte geri alın. {egg.name}–{potato.name} karışımını dökün ve eşit bir kalınlığa bastırın. Kenarlar tamamen tutuncaya ve tavayı salladığınızda merkez büyük ölçüde katılaşmış ama hafifçe titrek kalana kadar 4-5 dakika karıştırmadan pişirin.",
          },
        },
      },
      {
        id: "flip",
        template:
          "Place a large flat plate (slightly wider than the pan) face-down over the pan. Holding both firmly, flip in one decisive motion — the {egg.name}–{potato.name} tortilla lands on the plate, browned side up. Slide it back into the pan, uncooked side down, and {egg.safetyCue}.",
        translations: {
          tr: {
            template:
              "Tavanın üzerine (tavadan biraz büyük) büyük düz bir tabağı ters çevirin. İkisini sıkıca tutarak tek kararlı bir hamleyle çevirin — {egg.name}–{potato.name} tortillası tabağa düşer, kızarmış yüzü yukarıya gelir. Pişmemiş yüzü aşağı bakacak şekilde geri tavaya kaydırın ve {egg.safetyCue}.",
          },
        },
      },
      {
        id: "rest-serve",
        template:
          "Slide the tortilla onto a board. Rest for at least 5 minutes before cutting into wedges — it continues to set as it cools and is best eaten warm or at room temperature.",
        translations: {
          tr: {
            template:
              "Tortillayı bir tahta üzerine kaydırın. Dilimlemeden önce en az 5 dakika dinlendirin — soğudukça katılaşmaya devam eder ve sıcak ya da oda sıcaklığında en iyisidir.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{potato.name} ve {allium.name} ile İspanyol tortillası",
      },
    },
  },
];
